// Members page (ADR-179 P3 / section 7-E): list the account's members,
// invite a new one (returning a one-time token/URL to copy — no email
// infrastructure), remove a member, and change a member's role. Every
// mutation here has its REAL security boundary server-side
// (server/customer_membership.go's role gates); this page's role-based
// gating of which controls are even rendered is UX only, not the
// enforcement — a member with no owner/admin role simply gets a
// PermissionDenied if it somehow reached a mutating call, but the UI
// avoids offering that button in the first place.
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto the shared genesis.ts tokens + glass-card idiom.
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as pb from '@/generated/console_pb'
import { useCustomerAuth } from '@/lib/auth/auth-context'
import { listMembers, inviteMember, removeMember, setMemberRole } from '@/lib/auth/customer-api'
import { DARK, GREY_200, GREY_300, GREY_400, GREY_500, WHITE, errorBlockStyle, primaryButtonCompactStyle } from '@/lib/genesis'

function roleLabel(role: pb.AccountRole): string {
  switch (role) {
    case pb.AccountRole.ACCOUNT_ROLE_OWNER:
      return 'Owner'
    case pb.AccountRole.ACCOUNT_ROLE_ADMIN:
      return 'Admin'
    default:
      return 'Member'
  }
}

export function PortalMembersPage() {
  const { principal } = useCustomerAuth()
  const queryClient = useQueryClient()
  const canManage = principal?.role === 'owner' || principal?.role === 'admin'
  const isOwner = principal?.role === 'owner'

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal-members'],
    queryFn: () => listMembers(),
  })

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<pb.AccountRole>(pb.AccountRole.ACCOUNT_ROLE_MEMBER)
  const [inviteResult, setInviteResult] = useState<{ token: string } | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviting, setInviting] = useState(false)

  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['portal-members'] })
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault()
    setInviteError(null)
    setInviting(true)
    try {
      const resp = await inviteMember(inviteEmail.trim(), inviteRole)
      setInviteResult({ token: resp.getInviteToken() })
      setInviteEmail('')
      refresh()
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to invite member.')
    } finally {
      setInviting(false)
    }
  }

  async function handleConfirmRemove(userId: string) {
    setActionError(null)
    try {
      await removeMember(userId)
      refresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove member.')
    } finally {
      setPendingRemoveId(null)
    }
  }

  async function handleRoleChange(userId: string, newRole: pb.AccountRole) {
    setActionError(null)
    try {
      await setMemberRole(userId, newRole)
      refresh()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to change role.')
    }
  }

  const members = data?.getMembersList() ?? []
  const inviteURL =
    inviteResult && typeof window !== 'undefined'
      ? `${window.location.origin}/accept-invite?token=${encodeURIComponent(inviteResult.token)}`
      : null

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-display text-xl font-semibold" style={{ color: WHITE }}>Members</h1>
        {canManage && (
          <button
            type="button"
            onClick={() => {
              setInviteOpen(true)
              setInviteResult(null)
              setInviteError(null)
            }}
            className="rounded-lg px-3 py-1.5 text-sm font-semibold"
            style={primaryButtonCompactStyle}
          >
            Invite member
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading members…</p>}
      {isError && (
        <div className="p-3" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>Failed to load members.</p>
        </div>
      )}
      {actionError && (
        <div className="mb-3 p-3" style={errorBlockStyle}>
          <p className="text-sm" style={{ color: WHITE }}>{actionError}</p>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="glass-card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead style={{ color: GREY_400 }}>
              <tr>
                <th className="px-3 py-2 font-medium">Email</th>
                <th className="px-3 py-2 font-medium">Role</th>
                <th className="px-3 py-2 font-medium">Joined</th>
                {canManage && <th className="px-3 py-2 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const role = m.getRole()
                const isTargetOwner = role === pb.AccountRole.ACCOUNT_ROLE_OWNER
                const isSelf = m.getUserId() === principal?.userId
                // Only an owner may grant/revoke admin (server-enforced —
                // see SetMemberRole's doc); the UI hides that specific
                // toggle for a non-owner admin rather than offering a
                // button that would always PermissionDeny.
                const canToggleAdmin = isOwner && !isTargetOwner && !isSelf
                const canRemove = canManage && !isTargetOwner && !isSelf

                return (
                  <tr key={m.getUserId()} style={{ borderTop: `1px solid ${DARK}`, color: GREY_200 }}>
                    <td className="px-3 py-2">{m.getEmail()}</td>
                    <td className="px-3 py-2">{roleLabel(role)}</td>
                    <td className="px-3 py-2" style={{ color: GREY_400 }}>
                      {m.getCreatedAt()
                        ? new Date((m.getCreatedAt()?.getSeconds() ?? 0) * 1000).toLocaleDateString()
                        : '—'}
                    </td>
                    {canManage && (
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {canToggleAdmin &&
                            (role === pb.AccountRole.ACCOUNT_ROLE_ADMIN ? (
                              <button
                                type="button"
                                onClick={() => handleRoleChange(m.getUserId(), pb.AccountRole.ACCOUNT_ROLE_MEMBER)}
                                className="glass-button rounded-md px-2 py-1 text-xs"
                                style={{ color: GREY_300 }}
                              >
                                Revoke admin
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleRoleChange(m.getUserId(), pb.AccountRole.ACCOUNT_ROLE_ADMIN)}
                                className="glass-button rounded-md px-2 py-1 text-xs"
                                style={{ color: GREY_300 }}
                              >
                                Make admin
                              </button>
                            ))}
                          {canRemove &&
                            (pendingRemoveId === m.getUserId() ? (
                              <span className="flex items-center gap-1 text-xs">
                                <span style={{ color: GREY_400 }}>Remove?</span>
                                <button
                                  type="button"
                                  onClick={() => handleConfirmRemove(m.getUserId())}
                                  className="rounded-md px-2 py-1"
                                  style={{ border: '1px solid #F27B76', color: '#F27B76' }}
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setPendingRemoveId(null)}
                                  className="glass-button rounded-md px-2 py-1"
                                  style={{ color: GREY_300 }}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setPendingRemoveId(m.getUserId())}
                                className="rounded-md px-2 py-1 text-xs"
                                style={{ border: '1px solid #F27B76', color: '#F27B76' }}
                              >
                                Remove
                              </button>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {inviteOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 p-4">
          <div className="glass-card w-full max-w-sm p-5">
            {!inviteResult ? (
              <form onSubmit={handleInvite}>
                <h2 className="mb-3 text-sm font-semibold" style={{ color: WHITE }}>Invite a member</h2>
                <p className="mb-3 text-xs" style={{ color: GREY_500 }}>
                  There is no email delivery yet — you'll get a one-time link to copy and send yourself.
                </p>
                <label htmlFor="invite-email" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
                  Email
                </label>
                <input
                  id="invite-email"
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="glass-input mb-3 w-full px-3 py-2 text-sm"
                  style={{ color: WHITE }}
                />
                <label htmlFor="invite-role" className="mb-1 block text-xs" style={{ color: GREY_400 }}>
                  Role
                </label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(Number(e.target.value) as pb.AccountRole)}
                  className="glass-input mb-4 w-full px-3 py-2 text-sm"
                  style={{ color: WHITE }}
                >
                  <option value={pb.AccountRole.ACCOUNT_ROLE_MEMBER}>Member</option>
                  <option value={pb.AccountRole.ACCOUNT_ROLE_ADMIN}>Admin</option>
                </select>
                {inviteError && (
                  <div className="mb-3 p-2" style={errorBlockStyle}>
                    <p className="text-xs" style={{ color: WHITE }}>{inviteError}</p>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setInviteOpen(false)}
                    className="glass-button rounded-md px-3 py-1.5 text-xs"
                    style={{ color: GREY_300 }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviting}
                    className="rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
                    style={primaryButtonCompactStyle}
                  >
                    {inviting ? 'Sending…' : 'Send invite'}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <h2 className="mb-2 text-sm font-semibold" style={{ color: WHITE }}>Invite created</h2>
                <p className="mb-3 text-xs" style={{ color: GREY_500 }}>
                  Copy this link and send it to the new member yourself — there is no email delivery yet.
                </p>
                <input
                  readOnly
                  value={inviteURL ?? ''}
                  onFocus={(e) => e.currentTarget.select()}
                  className="glass-input mb-4 w-full px-3 py-2 font-mono text-xs"
                  style={{ color: GREY_300 }}
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setInviteOpen(false)}
                    className="rounded-md px-3 py-1.5 text-xs font-semibold"
                    style={primaryButtonCompactStyle}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
